/**
 * The design's shared style helpers, copied value for value from Aithora ConsoleV3.dc.html.
 * Each returns the design's own CSS string (so it can be compared with the source by eye)
 * and `sx()` turns one into a React style object. Pages use these instead of writing their
 * own tab, pill or chip styles.
 *
 *   <a style={sx(pageTab(on))}>...</a>
 */
import { cssToStyle } from '@aithora/shared';
import type { CSSProperties } from 'react';

const cache = new Map<string, CSSProperties>();
/** A design style string as a React style object (memoised: the same string, the same object). */
export function sx(css: string): CSSProperties {
  let v = cache.get(css);
  if (!v) {
    v = cssToStyle(css) as CSSProperties;
    cache.set(css, v);
  }
  return v;
}

// ── Tabs ────────────────────────────────────────────────────────────────────
/** Page tabs under a page title (design tab(), L18947). */
export const pageTab = (on: boolean) =>
  'display:inline-block;white-space:nowrap;padding:11px 2px 12px;font-size:13px;font-weight:' + (on ? '700' : '500') +
  ';color:' + (on ? '#2F5C97' : '#54606C') + ';text-decoration:none;border-bottom:2.5px solid ' +
  (on ? '#2F5C97' : 'transparent') + ';margin-bottom:-1px;';

/** Tabs inside a block (design btabStyle(), L18892). */
export const blockTab = (on: boolean) =>
  'display:inline-block;padding:10px 2px 11px;font-size:13px;font-weight:' + (on ? '700' : '500') +
  ';color:' + (on ? '#2F5C97' : '#54606C') + ';text-decoration:none;border-bottom:2.5px solid ' +
  (on ? '#2F5C97' : 'transparent') + ';margin-bottom:-1px;';

/**
 * Underline tabs with a constant weight, so the active tab cannot widen (design Worklist
 * wTabs chip(), L37681; "same as Performance").
 */
export const underlineTab = (on: boolean) =>
  'display:inline-flex;align-items:center;gap:6px;min-height:40px;padding:0 1px;box-sizing:border-box;font-size:13.5px;font-weight:700;text-decoration:none;white-space:nowrap;border-bottom:2.5px solid ' +
  (on ? '#2F5C97;color:#22272E;' : 'transparent;color:#54606C;');

/** The count beside an underline tab; nothing for zero (design nChip(), L37684). */
export const underlineTabCount = (n: number, on: boolean) =>
  n === 0
    ? 'display:none;'
    : 'font-size:14px;font-weight:700;border-radius:4px;padding:1px 5px;font-variant-numeric:tabular-nums;' +
      (on ? 'background:rgba(79,138,214,.18);color:#2F5C97;' : 'background:rgba(34,39,46,.07);color:#54606C;');

/** The Help page's Guide / FAQ tabs (design hdVals tabStyle, L33654). */
export const helpTab = (on: boolean) =>
  'display:inline-flex;align-items:center;padding:0 1px 9px;margin-bottom:-1px;font-size:13.5px;text-decoration:none;white-space:nowrap;border-bottom:2.5px solid ' +
  (on ? '#2F5C97;color:#22272E;font-weight:700;' : 'transparent;color:#54606C;font-weight:500;');

/** A side-nav row (design navStyle(), L18954). */
export const navItem = (on: boolean) =>
  'display:flex;align-items:center;gap:11px;flex-wrap:nowrap;white-space:nowrap;padding:8px 11px;border-radius:8px;font-size:13px;text-decoration:none;font-weight:' +
  (on ? '600' : '500') + ';color:' + (on ? '#2F5C97' : '#3C454F') + ';background:' + (on ? 'rgba(79,138,214,.11)' : 'transparent') + ';';

// ── Pills, segments, chips, buttons (the common forms across the pages) ────
/** Soft pill switcher, tinted when on (design pill(), L20031 Residents). */
export const pillSoft = (on: boolean) =>
  'display:inline-flex;align-items:center;height:32px;padding:0 15px;border-radius:8px;font-size:13px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' +
  (on ? 'background:rgba(79,138,214,.15);color:#2F5C97;' : 'background:transparent;color:#54606C;');

/** Solid pill switcher, blue when on, a cream card when off (design pill(), L21737 Settings). */
export const pillSolid = (on: boolean) =>
  'display:inline-flex;align-items:center;height:32px;padding:0 15px;border-radius:8px;font-size:13px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' +
  (on ? 'background:#2F5C97;color:#fff;' : 'background:#FCFAF4;color:#54606C;border:1px solid rgba(34,39,46,.14);');

/** A sub-tab: a raised cream card when on (design sub(), L20033). */
export const subTab = (on: boolean) =>
  'display:inline-flex;align-items:center;height:28px;padding:0 12px;border-radius:7px;font-size:13px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' +
  (on ? 'background:#FCFAF4;color:#22272E;border:1px solid rgba(34,39,46,.16);' : 'background:transparent;color:#54606C;');

/** A small segmented control, 26px (design seg(), L20035). */
export const segSmall = (on: boolean) =>
  'display:inline-flex;align-items:center;height:26px;padding:0 11px;border-radius:6px;font-size:13px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' +
  (on ? 'background:#2F5C97;color:#fff;' : 'background:transparent;color:#54606C;');

/** A segmented control with outlined off segments, 28px (design seg(), L21739). */
export const segOutline = (on: boolean) =>
  'display:inline-flex;align-items:center;height:28px;padding:0 12px;border-radius:6px;font-size:13px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' +
  (on ? 'background:#2F5C97;color:#fff;' : 'background:transparent;color:#54606C;border:1px solid rgba(34,39,46,.14);');

/** A status chip, text on a tint (design chip(fg, bg), L20037). */
export const chip = (fg: string, bg: string) =>
  'font-size:14px;font-weight:700;letter-spacing:.07em;border-radius:5px;padding:2px 7px;flex:0 0 auto;white-space:nowrap;color:' + fg + ';background:' + bg + ';';

/** A 19px tag with tabular figures (design chipS/chipT, L22639 and L26922). */
export const tag19 = (fg: string, bg: string) =>
  'display:inline-flex;align-items:center;height:22px;padding:0 8px;border-radius:5px;font-variant-numeric:tabular-nums;font-size:14px;letter-spacing:.06em;font-weight:700;white-space:nowrap;color:' + fg + ';background:' + bg + ';';

/** The palette's provenance chip, 18px (design paletteVals chip(), L28767). */
export const provenanceChip = (fg: string, bg: string) =>
  'display:inline-flex;align-items:center;height:22px;padding:0 7px;border-radius:4px;font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.07em;white-space:nowrap;flex:0 0 auto;color:' + fg + ';background:' + bg + ';';

/**
 * A button: primary blue, soft blue, off (dashed, nothing to do), or a cream card
 * (design btn(kind), L21742; L20038 is the same without 'soft' and 'off').
 */
export const button = (kind: 'primary' | 'soft' | 'off' | 'plain') =>
  'display:inline-flex;align-items:center;justify-content:center;box-sizing:border-box;height:30px;padding:0 14px;border-radius:8px;font-size:13px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' +
  (kind === 'primary'
    ? 'background:#2F5C97;color:#fff;'
    : kind === 'soft'
      ? 'background:rgba(79,138,214,.1);color:#2F5C97;border:1px solid rgba(47,92,151,.3);'
      : kind === 'off'
        ? 'background:rgba(34,39,46,.05);color:#54606C;border:1px dashed rgba(34,39,46,.2);'
        : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.18);');

/** The big figure on a stat (design fig(c, px), L20040). */
export const figure = (c: string, px = 26) => 'font-size:' + px + 'px;font-weight:700;line-height:1;letter-spacing:-.015em;color:' + c + ';';
